import Component from '@glimmer/component';

import { action } from '@ember/object';
import didInsert from '@ember/render-modifiers/modifiers/did-insert';
import { once } from '@ember/runloop';
import { tracked } from '@glimmer/tracking';
import MultiselectCheckboxes from 'ember-multiselect-checkboxes/components/multiselect-checkboxes';

import CIcon from 'rise-ui/components/c-icon.gjs';
import ExpandingBlock from 'rise-ui/components/expanding-block.gjs';
import and from 'rise-ui/helpers/and.js';
import arrayConcat from 'rise-ui/helpers/array-concat.js';
import classMod from 'rise-ui/helpers/class-mod.js';
import classMods from 'rise-ui/helpers/class-mods.js';
import concatClasses from 'rise-ui/helpers/concat-classes.js';
import context from 'rise-ui/helpers/context.js';
import gt from 'rise-ui/helpers/gt.js';
import set from 'rise-ui/helpers/set.js';
import onResize from 'rise-ui/modifiers/on-resize.js';
import { getSize } from 'rise-ui/utils/dom.js';
import { call } from 'rise-ui/utils/function.js';

export default class extends Component {
  containerElement;
  contentElement;
  @tracked container;

  @action onResize() {
    once(this, '_onResize');
  }

  _onResize() {
    if (!this.containerElement || !this.contentElement) {
      return;
    }
    const { height: container } = getSize(this.containerElement);
    const { height: content } = getSize(this.contentElement);
    const { options = [] } = this.args;
    call(this.args.onResize, null, container < content, container, content, options.length > 1);
  }

  @action onContainerInsert(el) {
    this.containerElement = el;
    this.onResize();
  }

  @action onContentInsert(el) {
    this.contentElement = el;
    this.onResize();
  }

  onContainerResize = this.onResize;
  onContentResize = this.onResize;

  <template>
    {{#let
      (concatClasses @class "c-multiselect-grouped-checkboxes")
      (gt @options.length 1)
      as | classes showGroups |
    }}
    <div
      class={{concatClasses
        classes
        (if @modifier
          (classMod
            classes
            modifiers=(classMods
              (arrayConcat @modifier @modifiers filter="truthy")
            )
          )
        )
      }}
      {{didInsert this.onContainerInsert}}
      {{onResize this.onContainerResize}}
      ...attributes
    >
      <div
        {{didInsert this.onContentInsert}}
        {{onResize this.onContentResize}}
      >
        {{#each @options as | group |}}
          {{#let (context) as | ctx |}}
          {{#if (and showGroups group.name)}}
            <ExpandingBlock
              @expanded={{ctx.isExpanded}}
              @onToggle={{set ctx "isExpanded"}}
            >
              <:head>
                <div class="c-multiselect-grouped-checkboxes__group-name">
                  <CIcon
                    @icon={{if ctx.isExpanded "collapse-small" "expand-small"}}
                    @text={{if ctx.isExpanded "Expanded" "Collapsed"}}
                  />
                  {{group.name}}
                </div>
              </:head>
              <:body>
                <MultiselectCheckboxes
                  @class="c-multiselect-checkboxes c-multiselect-grouped-checkboxes__group u-small-indent"
                  @labelProperty={{@labelProperty}}
                  @valueProperty={{@valueProperty}}
                  @options={{group.options}}
                  @selection={{@selection}}
                  @onchange={{@onchange}}
                  @updateSelectionValue={{@updateSelectionValue}}
                  as | option isSelected index |
                >
                  {{yield option isSelected index group.name true}}
                </MultiselectCheckboxes>
              </:body>
            </ExpandingBlock>
          {{else}}
            <MultiselectCheckboxes
              @class="c-multiselect-checkboxes c-multiselect-grouped-checkboxes__group"
              @labelProperty={{@labelProperty}}
              @valueProperty={{@valueProperty}}
              @options={{group.options}}
              @selection={{@selection}}
              @onchange={{@onchange}}
              @updateSelectionValue={{@updateSelectionValue}}
              as | option isSelected index |
            >
              {{yield option isSelected index group.name false}}
            </MultiselectCheckboxes>
          {{/if}}
          {{/let}}
        {{/each}}
      </div>
    </div>
    {{/let}}
  </template>
}
